import { createContext, useContext, useState } from "react";

const dict = {
  fr: {
    nav: {
      expertises: "Expertises",
      methode: "Méthode",
      about: "À Propos",
      temoignages: "Témoignages",
      faq: "FAQ",
      cta: "Consultation privée",
    },
    hero: {
      eyebrow: "Zurich — Conseil indépendant en gestion et aux entreprises",
      lines: ["L'immobilier d'exception,", "structuré avec rigueur."],
      sub: "Columbus Management Consulting GmbH accompagne investisseurs privés, family offices et institutions dans l'acquisition, la structuration et la gestion d'actifs immobiliers de premier ordre — entre Suisse et Europe.",
      cta1: "Découvrir nos expertises",
      cta2: "Prendre rendez-vous",
      coords: "47.3769° N — 8.5417° E · ZÜRICH",
      est: "EST. 2011 · SWISS GMBH",
      reg: "CHE-486.721.940",
      scroll: "Défiler",
      card: {
        title: "Mandat privé",
        sub: "Conseil de niveau institutionnel, discrétion absolue.",
        items: ["Stratégie", "Structuration", "Conformité", "Exécution"],
      },
    },
    marquee: [
      "Investissement immobilier",
      "Structuration de transactions",
      "Family office",
      "Zurich",
      "Gestion d'actifs",
      "Conformité transfrontalière",
    ],
    stats: {
      eyebrow: "Confiance institutionnelle",
      title: "Des décisions qui portent.",
      sub: "La rigueur suisse au service du capital immobilier : chaque mandat est documenté, chiffré et piloté par un conseiller senior.",
      seal: "Registre du commerce suisse · CHE-486.721.940",
      items: [
        { prefix: "CHF ", value: 450, suffix: "M+", label: "Actifs conseillés" },
        { prefix: "", value: 16, suffix: "+", label: "Années d'expérience" },
        { prefix: "", value: 120, suffix: "+", label: "Transactions transfrontalières" },
        { prefix: "", value: 98, suffix: "%", label: "Clients renouvelés" },
      ],
    },
    services: {
      eyebrow: "Nos expertises",
      title: "Six métiers, une seule exigence.",
      sub: "Chaque mandat commence par l'écoute et se termine par une structure écrite, chiffrée et expliquée. Jamais de produit placé sans raison.",
      cta: "Discuter d'un mandat",
      items: [
        {
          title: "Conseil en investissement immobilier",
          desc: "Acquisition, arbitrage et allocation d'actifs résidentiels et commerciaux de premier ordre, en Suisse et en Europe.",
        },
        {
          title: "Structuration de transactions",
          desc: "Origination, due diligence et exécution de transactions complexes — du premier mandat à la signature chez le notaire.",
        },
        {
          title: "Gestion d'actifs & portefeuille",
          desc: "Pilotage de la performance, reporting institutionnel et optimisation continue de vos actifs immobiliers.",
        },
        {
          title: "Ingénierie financière & capitaux",
          desc: "Structures de financement, refinancement et architecture capitalistique adaptées à chaque mandat.",
        },
        {
          title: "Conformité transfrontalière",
          desc: "Navigation réglementaire CH · LU · FR : fiscalité, Lex Koller, LFAIE et coordination des conseils externes.",
        },
        {
          title: "Family office & patrimoine",
          desc: "Transmission, gouvernance et consolidation patrimoniale pour familles, entrepreneurs et investisseurs privés.",
        },
      ],
    },
    method: {
      eyebrow: "Notre méthode",
      title: "Un cap, une route, des points d'étape.",
      sub: "Quatre étapes, toujours les mêmes. Parce que la rigueur n'est pas une promesse, c'est un processus.",
      steps: [
        {
          title: "Diagnostic & audit patrimonial",
          desc: "Un audit complet et confidentiel de votre situation : actifs, structure, fiscalité, objectifs et contraintes de marché.",
        },
        {
          title: "Stratégie d'investissement sur-mesure",
          desc: "Un plan d'action chiffré, écrit et expliqué. Vous savez toujours pourquoi chaque décision est recommandée.",
        },
        {
          title: "Structuration & exécution",
          desc: "Nous exécutons à vos côtés : négociation, financement, coordination des conseils et signature, en toute transparence.",
        },
        {
          title: "Suivi & optimisation continue",
          desc: "Reporting régulier, points d'étape annuels et alertes dès qu'un événement de marché ou de vie change la donne.",
        },
      ],
    },
    about: {
      eyebrow: "Le cabinet",
      title: "Indépendant par principe, pas par posture.",
      quote:
        "« Nous ne conseillons à nos clients que ce que nous structurerions pour notre propre patrimoine. Cette règle n'a pas changé depuis 2010. »",
      body1:
        "Columbus Management Consulting GmbH est une société de conseil suisse indépendante, sans lien capitalistique avec aucune banque, aucun promoteur. Nos conseillers seniors restent directement impliqués dans chaque mandat, du diagnostic à l'exécution.",
      body2:
        "Depuis Zurich, nous coordonnons fiscalistes, notaires, banques et family offices pour que chaque structure soit cohérente, durable et transmissible.",
      hq: { label: "Siège — Zurich", address: "Susenbergstrasse 104, 8044 Zürich", coords: "47.3769° N — 8.5417° E" },
      badges: ["Conseiller senior dédié", "Confidentialité absolue", "Sans rétrocession"],
    },
    testimonials: {
      eyebrow: "Ils nous font confiance",
      title: "La confiance se mesure en années.",
      items: [
        {
          quote:
            "« Une clarté rare. Pour la première fois, nous comprenons exactement où va notre capital — et pourquoi. »",
          name: "Hélène D.",
          role: "Family office, Zurich — cliente depuis 2016",
        },
        {
          quote:
            "« Une transaction transfrontalière complexe, exécutée sans faute. Rigueur suisse, discrétion absolue. »",
          name: "Markus K.",
          role: "Investisseur institutionnel, Genève",
        },
        {
          quote:
            "« La transmission de notre portefeuille a été préparée trois ans en amont. Tout s'est déroulé comme prévu. »",
          name: "Famille R.",
          role: "Entrepreneurs, Suisse — clients depuis 2018",
        },
      ],
    },
    faq: {
      eyebrow: "Questions fréquentes",
      title: "Des réponses claires, avant même de nous rencontrer.",
      items: [
        {
          q: "Quels types de clients accompagnez-vous ?",
          a: "Investisseurs privés, family offices, entrepreneurs et institutions. Chaque mandat est scopé individuellement : acquisition d'un actif unique, construction de portefeuille ou restructuration patrimoniale complète.",
        },
        {
          q: "Quel est le ticket d'investissement minimum ?",
          a: "Nos mandats démarrent généralement à partir de CHF 1 million. En dessous, nous vous orientons avec la même honnêteté vers la solution la plus adaptée — même si elle n'est pas chez nous.",
        },
        {
          q: "Comment êtes-vous rémunérés ?",
          a: "Par des honoraires de mandat transparents, écrits et validés avant toute mission. Aucune rétrocession, aucune commission cachée : notre seul revenu vient de vous.",
        },
        {
          q: "Êtes-vous vraiment indépendants ?",
          a: "Oui. Columbus Management Consulting GmbH est une société suisse indépendante, détenue par ses associés, sans lien capitalistique avec des banques, promoteurs ou plateformes de distribution.",
        },
        {
          q: "Mes données sont-elles confidentielles ?",
          a: "Absolument. Accès contrôlé, documentation sécurisée et conformité nLPD / RGPD. Le premier rendez-vous est confidentiel et sans engagement.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Parlons de votre prochain actif.",
      sub: "Décrivez votre projet. Un conseiller senior vous répond sous 24 heures ouvrées — en toute confidentialité.",
      addressLabel: "Siège",
      address: "Susenbergstrasse 104, 8044 Zürich, Suisse",
      emailLabel: "Email",
      phoneLabel: "Téléphone",
      response: "Réponse sous 24 h ouvrées",
      form: {
        name: "Nom complet",
        namePh: "Jean Dupont",
        email: "Email",
        emailPh: "jean@exemple.ch",
        phone: "Téléphone",
        phonePh: "",
        type: "Profil investisseur",
        types: ["Investisseur privé", "Family office", "Institution / Fonds", "Entreprise", "Autre"],
        message: "Votre message",
        messagePh: "Décrivez votre projet ou votre besoin en quelques lignes…",
        submit: "Envoyer la demande",
        sending: "Envoi en cours…",
        success: "Demande envoyée. Un conseiller senior vous répondra sous 24 h.",
        error: "Une erreur est survenue. Merci de réessayer.",
      },
    },
    footer: {
      tagline: "Conseil en investissement immobilier & structuration de patrimoine.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      legalTitle: "Mentions",
      legal: "Columbus Management Consulting GmbH · CHE-486.721.940 · Registre du commerce du canton de Zurich",
      admin: "Accès admin",
      rights: "© 2011-2026 Columbus Management Consulting GmbH. Tous droits réservés.",
      privacy: "Confidentialité · Mentions légales",
    },
  },
  en: {
    nav: {
      expertises: "Expertise",
      methode: "Method",
      about: "About",
      temoignages: "Testimonials",
      faq: "FAQ",
      cta: "Private consultation",
    },
    hero: {
      eyebrow: "Zurich — Independent business and management advisory",
      lines: ["Exceptional real estate,", "structured with rigour."],
      sub: "Columbus Management Consulting GmbH advises private investors, family offices and institutions on the acquisition, structuring and management of prime real estate assets — across Switzerland and Europe.",
      cta1: "Explore our expertise",
      cta2: "Book a meeting",
      coords: "47.3769° N — 8.5417° E · ZÜRICH",
      est: "EST. 2011 · SWISS GMBH",
      reg: "CHE-486.721.940",
      scroll: "Scroll",
      card: {
        title: "Private mandate",
        sub: "Institutional-grade advisory, absolute discretion.",
        items: ["Strategy", "Structuring", "Compliance", "Execution"],
      },
    },
    marquee: [
      "Real estate investment",
      "Transaction structuring",
      "Family office",
      "Zurich",
      "Asset management",
      "Cross-border compliance",
    ],
    stats: {
      eyebrow: "Institutional confidence",
      title: "Decisions that carry weight.",
      sub: "Swiss rigour applied to real estate capital: every mandate is documented, quantified and led by a senior advisor.",
      seal: "Swiss commercial register · CHE-486.721.940",
      items: [
        { prefix: "CHF ", value: 450, suffix: "M+", label: "Assets advised" },
        { prefix: "", value: 16, suffix: "+", label: "Years of experience" },
        { prefix: "", value: 120, suffix: "+", label: "Cross-border transactions" },
        { prefix: "", value: 98, suffix: "%", label: "Client retention" },
      ],
    },
    services: {
      eyebrow: "Our expertise",
      title: "Six disciplines, one standard.",
      sub: "Every mandate begins with listening and ends with a written, quantified, explained structure. Never a product placed without reason.",
      cta: "Discuss a mandate",
      items: [
        {
          title: "Real estate investment advisory",
          desc: "Acquisition, arbitrage and allocation of prime residential and commercial assets across Switzerland and Europe.",
        },
        {
          title: "Transaction structuring",
          desc: "Origination, due diligence and execution of complex transactions — from first mandate to signature at the notary.",
        },
        {
          title: "Asset & portfolio management",
          desc: "Performance steering, institutional-grade reporting and continuous optimisation of your real estate assets.",
        },
        {
          title: "Financing & capital structuring",
          desc: "Financing structures, refinancing and capital architecture tailored to each mandate.",
        },
        {
          title: "Cross-border compliance",
          desc: "Regulatory navigation across CH · LU · FR: taxation, Lex Koller, AWG and coordination of external counsel.",
        },
        {
          title: "Family office & wealth",
          desc: "Succession, governance and wealth consolidation for families, entrepreneurs and private investors.",
        },
      ],
    },
    method: {
      eyebrow: "Our method",
      title: "A heading, a route, milestones.",
      sub: "Four steps, always the same. Because rigour is not a promise — it is a process.",
      steps: [
        {
          title: "Diagnostic & wealth audit",
          desc: "A complete, confidential audit of your situation: assets, structure, taxation, objectives and market constraints.",
        },
        {
          title: "Bespoke investment strategy",
          desc: "A written, quantified and explained action plan. You always know why each decision is recommended.",
        },
        {
          title: "Structuring & execution",
          desc: "We execute alongside you: negotiation, financing, counsel coordination and signing, in full transparency.",
        },
        {
          title: "Monitoring & continuous optimisation",
          desc: "Regular reporting, annual reviews and alerts the moment a market or life event changes the equation.",
        },
      ],
    },
    about: {
      eyebrow: "The firm",
      title: "Independent by principle, not by posture.",
      quote:
        "\u00AB We only advise our clients on what we would structure for our own wealth. That rule has not changed since 2010. \u00BB",
      body1:
        "Columbus Management Consulting GmbH is an independent Swiss advisory firm, with no equity ties to any bank or developer. Our senior advisors remain directly involved in every mandate, from diagnosis to execution.",
      body2:
        "From Zurich, we coordinate tax advisers, notaries, banks and family offices so that every structure is coherent, durable and transferable.",
      hq: { label: "Headquarters — Zurich", address: "Susenbergstrasse 104, 8044 Zürich", coords: "47.3769° N — 8.5417° E" },
      badges: ["Dedicated senior advisor", "Absolute confidentiality", "No retrocessions"],
    },
    testimonials: {
      eyebrow: "Trusted by",
      title: "Trust is measured in years.",
      items: [
        {
          quote:
            "\u00AB Rare clarity. For the first time, we understand exactly where our capital goes — and why. \u00BB",
          name: "Hélène D.",
          role: "Family office, Zurich — client since 2016",
        },
        {
          quote:
            "\u00AB A complex cross-border transaction, executed flawlessly. Swiss rigour, absolute discretion. \u00BB",
          name: "Markus K.",
          role: "Institutional investor, Geneva",
        },
        {
          quote:
            "\u00AB The transfer of our portfolio was prepared three years in advance. Everything unfolded as planned. \u00BB",
          name: "Famille R.",
          role: "Entrepreneurs, Switzerland — clients since 2018",
        },
      ],
    },
    faq: {
      eyebrow: "Frequently asked questions",
      title: "Clear answers, before we even meet.",
      items: [
        {
          q: "What types of clients do you advise?",
          a: "Private investors, family offices, entrepreneurs and institutions. Every mandate is scoped individually: single-asset acquisition, portfolio build-out or full wealth restructuring.",
        },
        {
          q: "What is the minimum investment ticket?",
          a: "Our mandates typically start at CHF 1 million. Below that, we point you — with the same honesty — towards the most suitable solution, even if it is not with us.",
        },
        {
          q: "How are you compensated?",
          a: "Through transparent mandate fees, written and approved before any engagement. No retrocessions, no hidden commissions: our only income comes from you.",
        },
        {
          q: "Are you truly independent?",
          a: "Yes. Columbus Management Consulting GmbH is an independent Swiss company, owned by its partners, with no equity ties to banks, developers or distribution platforms.",
        },
        {
          q: "Is my data confidential?",
          a: "Absolutely. Controlled access, secure documentation and nFADP / GDPR compliance. The first meeting is confidential and without obligation.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's discuss your next asset.",
      sub: "Tell us about your project. A senior advisor will reply within one business day — in full confidentiality.",
      addressLabel: "Headquarters",
      address: "Susenbergstrasse 104, 8044 Zürich, Switzerland",
      emailLabel: "Email",
      phoneLabel: "Phone",
      response: "Reply within one business day",
      form: {
        name: "Full name",
        namePh: "John Smith",
        email: "Email",
        emailPh: "john@example.ch",
        phone: "Phone",
        phonePh: "",
        type: "Investor profile",
        types: ["Private investor", "Family office", "Institution / Fund", "Corporate", "Other"],
        message: "Your message",
        messagePh: "Describe your project or requirement in a few lines…",
        submit: "Send request",
        sending: "Sending…",
        success: "Request sent. A senior advisor will reply within one business day.",
        error: "Something went wrong. Please try again.",
      },
    },
    footer: {
      tagline: "Real estate investment advisory & wealth structuring.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      legalTitle: "Legal",
      legal: "Columbus Management Consulting GmbH · CHE-486.721.940 · Commercial register of the canton of Zurich",
      admin: "Admin access",
      rights: "© 2011-2026 Columbus Management Consulting GmbH. All rights reserved.",
      privacy: "Privacy · Legal notice",
    },
  },
  de: {
    nav: {
      expertises: "Expertisen",
      methode: "Methode",
      about: "Über uns",
      temoignages: "Referenzen",
      faq: "FAQ",
      cta: "Private Beratung",
    },
    hero: {
      eyebrow: "Zürich — Unabhängige Unternehmens- und Managementberatung",
      lines: ["Aussergewöhnliche Immobilien,", "mit Präzision strukturiert."],
      sub: "Die Columbus Management Consulting GmbH begleitet private Investoren, Family Offices und Institutionen bei Erwerb, Strukturierung und Verwaltung erstklassiger Immobilienanlagen — in der Schweiz und Europa.",
      cta1: "Unsere Expertisen entdecken",
      cta2: "Termin vereinbaren",
      coords: "47.3769° N — 8.5417° O · ZÜRICH",
      est: "EST. 2011 · SWISS GMBH",
      reg: "CHE-486.721.940",
      scroll: "Scrollen",
      card: {
        title: "Privates Mandat",
        sub: "Beratung auf institutionellem Niveau, absolute Diskretion.",
        items: ["Strategie", "Strukturierung", "Compliance", "Ausführung"],
      },
    },
    marquee: [
      "Immobilieninvestitionen",
      "Transaktionsstrukturierung",
      "Family Office",
      "Zürich",
      "Vermögensverwaltung",
      "Grenzüberschreitende Compliance",
    ],
    stats: {
      eyebrow: "Institutionelles Vertrauen",
      title: "Entscheidungen mit Gewicht.",
      sub: "Schweizer Präzision im Dienst des Immobilienkapitals: Jedes Mandat wird dokumentiert, beziffert und von einem Senior-Berater geführt.",
      seal: "Schweizer Handelsregister · CHE-486.721.940",
      items: [
        { prefix: "CHF ", value: 450, suffix: "M+", label: "Beratene Vermögenswerte" },
        { prefix: "", value: 16, suffix: "+", label: "Jahre Erfahrung" },
        { prefix: "", value: 120, suffix: "+", label: "Grenzüberschreitende Transaktionen" },
        { prefix: "", value: 98, suffix: "%", label: "Kundenbindung" },
      ],
    },
    services: {
      eyebrow: "Unsere Expertisen",
      title: "Sechs Disziplinen, ein Anspruch.",
      sub: "Jedes Mandat beginnt mit Zuhören und endet mit einer schriftlichen, bezifferten und erklärten Struktur. Nie ein Produkt ohne Grund.",
      cta: "Mandat besprechen",
      items: [
        {
          title: "Beratung für Immobilieninvestitionen",
          desc: "Erwerb, Arbitrage und Allokation erstklassiger Wohn- und Gewerbeimmobilien in der Schweiz und Europa.",
        },
        {
          title: "Transaktionsstrukturierung",
          desc: "Origination, Due Diligence und Ausführung komplexer Transaktionen — vom ersten Mandat bis zur Unterschrift beim Notar.",
        },
        {
          title: "Asset- & Portfoliomanagement",
          desc: "Performance-Steuerung, institutionelles Reporting und laufende Optimierung Ihrer Immobilienanlagen.",
        },
        {
          title: "Finanzierungs- & Kapitalstrukturierung",
          desc: "Finanzierungsstrukturen, Refinanzierung und Kapitalarchitektur, massgeschneidert für jedes Mandat.",
        },
        {
          title: "Grenzüberschreitende Compliance",
          desc: "Regulatorische Navigation CH · LU · FR: Steuern, Lex Koller, BewG und Koordination externer Berater.",
        },
        {
          title: "Family Office & Vermögen",
          desc: "Nachfolge, Governance und Vermögenskonsolidierung für Familien, Unternehmer und Privatinvestoren.",
        },
      ],
    },
    method: {
      eyebrow: "Unsere Methode",
      title: "Ein Kurs, eine Route, Etappen.",
      sub: "Vier Schritte, immer dieselben. Denn Präzision ist kein Versprechen — sie ist ein Prozess.",
      steps: [
        {
          title: "Diagnose & Vermögensaudit",
          desc: "Ein vollständiges, vertrauliches Audit Ihrer Situation: Vermögenswerte, Struktur, Steuern, Ziele und Marktrahmen.",
        },
        {
          title: "Massgeschneiderte Anlagestrategie",
          desc: "Ein schriftlicher, bezifferter und erklärter Aktionsplan. Sie wissen stets, warum jede Entscheidung empfohlen wird.",
        },
        {
          title: "Strukturierung & Ausführung",
          desc: "Wir setzen an Ihrer Seite um: Verhandlung, Finanzierung, Koordination der Berater und Unterschrift — transparent.",
        },
        {
          title: "Monitoring & laufende Optimierung",
          desc: "Regelmässiges Reporting, jährliche Reviews und Warnungen, sobald ein Markt- oder Lebensereignis die Lage ändert.",
        },
      ],
    },
    about: {
      eyebrow: "Das Unternehmen",
      title: "Unabhängig aus Überzeugung, nicht aus Pose.",
      quote:
        "« Wir empfehlen unseren Kunden nur, was wir auch für unser eigenes Vermögen strukturieren würden. An dieser Regel hat sich seit 2011 nichts geändert. »",
      body1:
        "Die Columbus Management Consulting GmbH ist eine unabhängige Schweizer Beratungsgesellschaft — ohne Beteiligungsverflechtungen mit Banken oder Entwicklern. Unsere Senior-Berater bleiben bei jedem Mandat direkt involviert, von der Diagnose bis zur Ausführung.",
      body2:
        "Von Zürich aus koordinieren wir Steuerberater, Notare, Banken und Family Offices, damit jede Struktur kohärent, dauerhaft und übertragbar ist.",
      hq: { label: "Hauptsitz — Zürich", address: "Susenbergstrasse 104, 8044 Zürich", coords: "47.3769° N — 8.5417° O" },
      badges: ["Dedizierter Senior-Berater", "Absolute Vertraulichkeit", "Keine Retrozessionen"],
    },
    testimonials: {
      eyebrow: "Sie vertrauen uns",
      title: "Vertrauen misst sich in Jahren.",
      items: [
        {
          quote:
            "« Seltene Klarheit. Zum ersten Mal verstehen wir genau, wohin unser Kapital fliesst — und warum. »",
          name: "Hélène D.",
          role: "Family Office, Zürich — Kundin seit 2016",
        },
        {
          quote:
            "« Eine komplexe grenzüberschreitende Transaktion, fehlerfrei ausgeführt. Schweizer Präzision, absolute Diskretion. »",
          name: "Markus K.",
          role: "Institutioneller Investor, Genf",
        },
        {
          quote:
            "« Die Übertragung unseres Portfolios wurde drei Jahre im Voraus vorbereitet. Alles verlief wie geplant. »",
          name: "Familie R.",
          role: "Unternehmer, Schweiz — Kunden seit 2018",
        },
      ],
    },
    faq: {
      eyebrow: "Häufige Fragen",
      title: "Klare Antworten, noch vor dem ersten Treffen.",
      items: [
        {
          q: "Welche Kunden begleiten Sie?",
          a: "Privatinvestoren, Family Offices, Unternehmer und Institutionen. Jedes Mandat wird individuell definiert: Erwerb eines einzelnen Objekts, Portfolioaufbau oder vollständige Vermögensrestrukturierung.",
        },
        {
          q: "Wie hoch ist das minimale Anlageticket?",
          a: "Unsere Mandate beginnen in der Regel ab CHF 1 Million. Darunter verweisen wir Sie mit derselben Ehrlichkeit auf die passendste Lösung — auch wenn sie nicht bei uns liegt.",
        },
        {
          q: "Wie werden Sie vergütet?",
          a: "Durch transparente Mandatshonorare, die vor jeder Aufgabe schriftlich festgehalten und genehmigt werden. Keine Retrozessionen, keine versteckten Provisionen: Unser einziges Einkommen kommt von Ihnen.",
        },
        {
          q: "Sind Sie wirklich unabhängig?",
          a: "Ja. Die Columbus Management Consulting GmbH ist eine unabhängige Schweizer Gesellschaft im Besitz ihrer Partner — ohne Beteiligungen von Banken, Entwicklern oder Vertriebsplattformen.",
        },
        {
          q: "Sind meine Daten vertraulich?",
          a: "Absolut. Kontrollierter Zugriff, gesicherte Dokumentation und Konformität mit nDSG / DSGVO. Das erste Gespräch ist vertraulich und unverbindlich.",
        },
      ],
    },
    contact: {
      eyebrow: "Kontakt",
      title: "Sprechen wir über Ihre nächste Anlage.",
      sub: "Beschreiben Sie Ihr Projekt. Ein Senior-Berater antwortet innerhalb von 24 Arbeitsstunden — absolut vertraulich.",
      addressLabel: "Hauptsitz",
      address: "Susenbergstrasse 104, 8044 Zürich, Schweiz",
      emailLabel: "E-Mail",
      phoneLabel: "Telefon",
      response: "Antwort innerhalb von 24 Arbeitsstunden",
      form: {
        name: "Vollständiger Name",
        namePh: "Max Muster",
        email: "E-Mail",
        emailPh: "max@beispiel.ch",
        phone: "Telefon",
        phonePh: "",
        type: "Investorenprofil",
        types: ["Privatinvestor", "Family Office", "Institution / Fonds", "Unternehmen", "Andere"],
        message: "Ihre Nachricht",
        messagePh: "Beschreiben Sie Ihr Projekt oder Anliegen in wenigen Zeilen…",
        submit: "Anfrage senden",
        sending: "Wird gesendet…",
        success: "Anfrage gesendet. Ein Senior-Berater antwortet innerhalb eines Arbeitstages.",
        error: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
      },
    },
    footer: {
      tagline: "Beratung für Immobilieninvestitionen & Vermögensstrukturierung.",
      navTitle: "Navigation",
      contactTitle: "Kontakt",
      legalTitle: "Rechtliches",
      legal: "Columbus Management Consulting GmbH · CHE-486.721.940 · Handelsregister des Kantons Zürich",
      admin: "Admin-Zugang",
      rights: "© 2011-2026 Columbus Management Consulting GmbH. Alle Rechte vorbehalten.",
      privacy: "Datenschutz · Impressum",
    },
  },
};

const LanguageContext = createContext(null);

const LANGS = ["fr", "en", "de"];

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem("columbus_lang");
    return LANGS.includes(stored) ? stored : "fr";
  });
  const setLang = (l) => {
    if (!LANGS.includes(l)) return;
    localStorage.setItem("columbus_lang", l);
    setLangState(l);
  };
  return (
    <LanguageContext.Provider value={{ lang, setLang, t: dict[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
